import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, compose, withApollo } from 'react-apollo';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import gql from 'graphql-tag';
import cx from 'classnames';

// Stripe Elements - Modern PaymentElement library
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { payment } from '../../../config';

// Lucide React Icons
import {
  Sparkles,
  X,
  CreditCard,
  Smartphone,
  Building2,
  Zap,
  Shield,
  RefreshCw,
  Star,
  Calendar,
  Lock,
  IdCard,
  Landmark,
  Phone,
  Hash
} from 'lucide-react';

import messages from '../../../locale/messages';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import { COMMON_TEXT_COLOR } from '../../../constants/index';

import s from './FeaturedModal.css';

// Load Stripe outside component to avoid recreating on every render
let stripePromise = null;
const getStripePromise = () => {
  if (!stripePromise && payment?.stripe?.publishableKey) {
    stripePromise = loadStripe(payment.stripe.publishableKey);
  }
  return stripePromise;
};

// Stripe appearance for PaymentElement
const stripeAppearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0056b3',
    colorBackground: '#ffffff',
    colorText: '#1F2937',
    colorDanger: '#df1b41',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
  },
  rules: {
    '.Input': {
      border: '1px solid #E5E7EB',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '1px solid #0056b3',
      boxShadow: '0 0 0 1px #0056b3',
    },
    '.Label': {
      fontWeight: '500',
    },
  },
};

/**
 * Modern Stripe PaymentElement Form Component
 * Uses the new PaymentElement for all payment methods
 */
function StripePaymentForm({ onSubmit, isLoading, cardError }) {
  const stripe = useStripe();
  const elements = useElements();

  // Expose submit function to parent via window for the main button
  React.useEffect(() => {
    window.stripeSubmitPayment = async () => {
      if (!stripe || !elements) return;
      onSubmit(stripe, elements);
    };
    return () => { delete window.stripeSubmitPayment; };
  }, [stripe, elements, onSubmit]);

  // Layout "tabs" para mostrar opciones una al lado de la otra
  const paymentElementOptions = {
    layout: 'tabs'
  };

  return (
    <div className={s.stripeElementWrapper}>
      <PaymentElement 
        id="payment-element" 
        options={paymentElementOptions}
      />
      {cardError && (
        <p className={s.stripeError}>{cardError}</p>
      )}
    </div>
  );
}

// GraphQL Queries
const GET_COUNTRIES = gql`
  query getCountries {
    getCountries {
      id
      countryCode
      countryName
      isEnable
    }
  }
`;

const GET_FEATURED_SETTINGS = gql`
  query getFeaturedSettings {
    getFeaturedSettings {
      monthlyPrice
      premiumMonthlyPrice
      bidPercentage
      currency
    }
  }
`;

const GET_FEATURED_STATUS = gql`
  query getFeaturedStatus($listId: Int!) {
    getFeaturedStatus(listId: $listId) {
      id
      status
      monthlyFee
      commissionRate
      startDate
      endDate
      autoRenew
      daysRemaining
    }
  }
`;

const GET_FEATURED_COMPETITORS = gql`
  query getFeaturedCompetitors($listId: Int!, $proposedRate: Float) {
    getFeaturedCompetitors(listId: $listId, proposedRate: $proposedRate) {
      status
      currentPosition
      totalCompetitors
      currentCommissionRate
      minCommission
      maxCommission
      rateToBeFirst
      competitors {
        position
        listId
        listTitle
        commissionRate
        tier
        isCurrentUser
      }
    }
  }
`;

const CREATE_FEATURED_LISTING = gql`
  mutation createFeaturedListing(
    $listId: Int!
    $commissionRate: Float
    $autoRenew: Boolean
    $paymentType: Int!
    $transactionId: String
    $billingCountry: String
    $billingName: String
    $billingZipcode: String
    $tier: String
  ) {
    createFeaturedListing(
      listId: $listId
      commissionRate: $commissionRate
      autoRenew: $autoRenew
      paymentType: $paymentType
      transactionId: $transactionId
      billingCountry: $billingCountry
      billingName: $billingName
      billingZipcode: $billingZipcode
      tier: $tier
    ) {
      status
      featuredListingId
      errorMessage
    }
  }
`;

const CREATE_PAYPAL_ORDER = gql`
  mutation createFeaturedPayPalOrder(
    $listId: Int!, 
    $commissionRate: Float, 
    $autoRenew: Boolean,
    $billingCountry: String,
    $billingName: String,
    $billingZipcode: String,
    $tier: String
  ) {
    createFeaturedPayPalOrder(
      listId: $listId, 
      commissionRate: $commissionRate, 
      autoRenew: $autoRenew,
      billingCountry: $billingCountry,
      billingName: $billingName,
      billingZipcode: $billingZipcode,
      tier: $tier
    ) {
      status
      orderId
      approvalUrl
      errorMessage
    }
  }
`;

const UPDATE_AUTO_RENEW = gql`
  mutation updateFeaturedAutoRenew($featuredListingId: Int!, $autoRenew: Boolean!) {
    updateFeaturedAutoRenew(featuredListingId: $featuredListingId, autoRenew: $autoRenew) {
      status
      errorMessage
    }
  }
`;

const CANCEL_FEATURED = gql`
  mutation cancelFeaturedListing($featuredListingId: Int!) {
    cancelFeaturedListing(featuredListingId: $featuredListingId) {
      status
      errorMessage
    }
  }
`;

class FeaturedModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    listId: PropTypes.number.isRequired,
    listTitle: PropTypes.string,
    listPhoto: PropTypes.string,
    listCity: PropTypes.string,
    onSuccess: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      commissionRate: 0, // Commission percentage (0-40%)
      autoRenew: false,
      isLoading: false,
      showPaymentForm: false,
      featuredSettings: null,
      featuredStatus: null,
      // Competitors data
      competitors: [],
      currentPosition: null,
      totalCompetitors: 0,
      rateToBeFirst: 0,
      // Tier: 'standard' or 'premium'
      selectedTier: 'standard',
      // Payment method: 1=PayPal, 2=Stripe, 3=Credicard, 4=Zelle, 5=PagoMovil, 6=DebitoInmediato, 7=C2P
      paymentMethod: 2,
      // Stripe - PaymentElement
      stripeReady: false,
      cardError: null,
      clientSecret: null,
      paymentIntentId: null,
      stripeLoading: false,
      // All payment fields (matching PaymentForm.js)
      // Credicard (3)
      pan: '',
      cvv2: '',
      expdate: '',
      // Zelle (4)
      referencia: '',
      // Pago Móvil P2C (5)
      telefonoCliente: '',
      codigobancoCliente: '',
      telefonoComercio: '',
      codigobancoComercio: '',
      // Débito Inmediato (6)
      cuentaOrigen: '',
      telefonoOrigen: '',
      codigobancoOrigen: '',
      cuentaDestino: '',
      // C2P (7)
      telefono: '',
      codigobanco: '',
      codigoC2P: '',
      // Common
      cid: '',
      // Billing Address
      billingCountry: '', // Default to "Selecciona un país"
      billingName: '',
      billingAddress: '',
      billingCity: '',
      billingZipcode: '',
      // Countries list from DB
      countries: [],
    };
    // Stripe ref
    this.stripeFormRef = React.createRef();
  }

  // Get available payment methods based on country
  getAvailablePaymentMethods = () => {
    const { billingCountry } = this.state;
    const isVenezuela = billingCountry === 'VE';

    // Filter: ONLY Venezuela gets all 7 methods, everyone else (including no selection) gets PayPal & Stripe only
    return this.paymentMethods.filter(method => {
      if (isVenezuela) return true; // All methods for Venezuela
      return [1, 2].includes(method.id); // Only PayPal & Stripe for other countries
    });
  };

  // Payment methods available (matches PaymentForm.js)
  paymentMethods = [
    {
      id: 1,
      name: 'PayPal',
      desc: 'Pago seguro con PayPal',
      icon: CreditCard,
      color: '#0070ba'
    },
    {
      id: 2,
      name: 'Tarjeta de Crédito/Débito',
      desc: 'Visa, Mastercard, American Express (Stripe)',
      icon: CreditCard,
      color: '#635bff'
    },
    {
      id: 3,
      name: 'Tarjeta de Crédito de Venezuela',
      desc: 'Tarjeta de crédito en Bolívares',
      icon: CreditCard,
      color: '#00A651'
    },
    {
      id: 4,
      name: 'Zelle',
      desc: 'Transferencia bancaria USA',
      icon: Zap,
      color: '#6B21A8'
    },
    {
      id: 5,
      name: 'Pago Móvil P2C',
      desc: 'Transferencia interbancaria instantánea',
      icon: Smartphone,
      color: '#10B981'
    },
    {
      id: 6,
      name: 'Débito Inmediato',
      desc: 'Pago instantáneo con código',
      icon: Zap,
      color: '#EA580C'
    },
    {
      id: 7,
      name: 'Pago Móvil C2P',
      desc: 'Cuenta a Pago Móvil',
      icon: Smartphone,
      color: '#0891B2'
    },
  ];

  async componentDidMount() {
    await this.loadData();
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.listId !== this.props.listId ||
      (prevProps.isOpen !== this.props.isOpen && this.props.isOpen)) {
      await this.loadData();
    }
  }

  loadData = async () => {
    const { listId, client } = this.props;

    try {
      // Load featured settings
      const settingsResult = await client.query({
        query: GET_FEATURED_SETTINGS,
        fetchPolicy: 'network-only'
      });

      // Load featured status for this listing
      const statusResult = await client.query({
        query: GET_FEATURED_STATUS,
        variables: { listId },
        fetchPolicy: 'network-only'
      });

      // Load countries list
      const countriesResult = await client.query({
        query: GET_COUNTRIES,
        fetchPolicy: 'cache-first'
      });

      this.setState({
        featuredSettings: settingsResult?.data?.getFeaturedSettings,
        featuredStatus: statusResult?.data?.getFeaturedStatus,
        autoRenew: statusResult?.data?.getFeaturedStatus?.autoRenew || false,
        countries: countriesResult?.data?.getCountries || [],
      }, () => {
      });
    } catch (error) {
      console.error('Error loading featured data:', error);
    }
  };


  handleCommissionChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    // Clamp between 0 and 40
    const clampedValue = Math.max(0, Math.min(40, value));
    this.setState({ commissionRate: clampedValue }, () => {
      // Update position preview when commission changes
      this.loadCompetitors(clampedValue);
    });
  };

  loadCompetitors = async (proposedRate) => {
    const { listId, client } = this.props;
    try {
      const result = await client.query({
        query: GET_FEATURED_COMPETITORS,
        variables: { listId, proposedRate },
        fetchPolicy: 'network-only'
      });

      if (result?.data?.getFeaturedCompetitors?.status === 'success') {
        const data = result.data.getFeaturedCompetitors;
        this.setState({
          competitors: data.competitors || [],
          currentPosition: data.currentPosition,
          totalCompetitors: data.totalCompetitors,
          rateToBeFirst: data.rateToBeFirst
        });
      }
    } catch (error) {
      console.error('Error loading competitors:', error);
    }
  };

  handleAutoRenewChange = async () => {
    const { featuredStatus } = this.state;
    const newAutoRenew = !this.state.autoRenew;
    this.setState({ autoRenew: newAutoRenew });

    // If already featured, update in database
    if (featuredStatus && featuredStatus.status === 'active') {
      try {
        const { client } = this.props;
        await client.mutate({
          mutation: UPDATE_AUTO_RENEW,
          variables: {
            featuredListingId: featuredStatus.id,
            autoRenew: newAutoRenew
          }
        });
      } catch (error) {
        console.error('Error updating auto-renew:', error);
      }
    }
  };

  handlePaymentMethodChange = (methodId) => {
    this.setState({ paymentMethod: methodId }, () => {
      // Create PaymentIntent when Stripe is selected
      if (methodId === 2) {
        this.createStripePaymentIntent();
      }
    });
  };

  // Create Stripe PaymentIntent for Featured payment
  createStripePaymentIntent = async () => {
    const { listId, userId, userEmail, listTitle } = this.props;
    const { featuredSettings, selectedTier } = this.state;
    
    if (!featuredSettings) return;
    
    const monthlyPrice = selectedTier === 'premium'
      ? (featuredSettings.premiumMonthlyPrice || 199)
      : (featuredSettings.monthlyPrice || 99);
    
    this.setState({ stripeLoading: true, cardError: null, clientSecret: null });
    
    try {
      const response = await fetch('/create-featured-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: monthlyPrice,
          currency: featuredSettings.currency || 'USD',
          listId,
          userId,
          userEmail,
          tier: selectedTier,
          listTitle
        })
      });
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      this.setState({
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId,
        stripeLoading: false
      });
    } catch (error) {
      console.error('[FeaturedModal] Error creating PaymentIntent:', error);
      this.setState({
        stripeLoading: false,
        cardError: error.message || 'Error al preparar el pago'
      });
    }
  };

  handlePaymentFieldChange = (field, value) => {
    this.setState({ [field]: value });
  };

  // Handle Stripe payment with modern PaymentElement and redirect
  handleStripePayment = async (stripe, elements) => {
    const { listId } = this.props;
    const { commissionRate, autoRenew, selectedTier, billingCountry, billingName, billingZipcode } = this.state;
    
    if (!billingCountry) {
      this.setState({ cardError: 'Por favor selecciona tu país' });
      return;
    }
    
    this.setState({ isLoading: true, cardError: null });
    
    // Save featured data to sessionStorage before redirect
    const featuredData = {
      listId,
      commissionRate,
      autoRenew,
      tier: selectedTier,
      billingCountry,
      billingName,
      billingZipcode
    };
    
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('stripeFeaturedData', JSON.stringify(featuredData));
    }
    
    // Build return URL
    const returnUrl = typeof window !== 'undefined' 
      ? `${window.location.origin}/featured/stripe/complete`
      : 'https://navegaapp.com/featured/stripe/complete';
    
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    });
    
    // This point will only be reached if there is an immediate error
    if (error) {
      this.setState({ 
        isLoading: false, 
        cardError: error.type === 'card_error' || error.type === 'validation_error' 
          ? error.message 
          : 'Error procesando el pago. Intenta de nuevo.'
      });
      // Clear sessionStorage on error
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('stripeFeaturedData');
      }
    }
  };

  renderPaymentForm = () => {
    const {
      paymentMethod,
      // Stripe
      cardError,
      // Credicard (3)
      pan, cvv2, expdate,
      // Zelle (4)
      referencia,
      // Pago Móvil (5)
      telefonoCliente, codigobancoCliente, telefonoComercio, codigobancoComercio,
      // Débito Inmediato (6)
      cuentaOrigen, telefonoOrigen, codigobancoOrigen, cuentaDestino,
      // C2P (7)
      telefono, codigobanco, codigoC2P,
      // Common
      cid
    } = this.state;

    // PayPal (id: 1)
    if (paymentMethod === 1) {
      return (
        <div className={s.paymentFormContainer}>
          <div className={s.paymentFormHeader}>
            <div className={s.paymentFormIcon} style={{ background: '#0070ba', color: 'white' }}><CreditCard size={20} /></div>
            <div>
              <p className={s.paymentFormTitle}>PayPal</p>
              <p className={s.paymentFormSubtitle}>Pago seguro con PayPal</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
              Al hacer clic en "Pagar", serás redirigido a PayPal para completar el pago de forma segura.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <img src="/SiteIcons/paypal-logo.png" alt="PayPal" style={{ height: '24px' }} onError={(e) => e.target.style.display = 'none'} />
            </div>
          </div>
        </div>
      );
    }

    // Stripe (id: 2) - Modern PaymentElement (sin header duplicado)
    if (paymentMethod === 2) {
      const { clientSecret, stripeLoading, isLoading } = this.state;
      
      return (
        <div className={s.stripePaymentContainer}>
          {stripeLoading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div className={s.spinner} style={{ margin: '0 auto 10px' }}></div>
              <p style={{ color: '#6B7280', fontSize: '14px' }}>Cargando...</p>
            </div>
          )}
          
          {!stripeLoading && clientSecret && (
            <Elements 
              stripe={getStripePromise()} 
              options={{ clientSecret, appearance: stripeAppearance, loader: 'auto' }}
            >
              <StripePaymentForm
                onSubmit={this.handleStripePayment}
                isLoading={isLoading}
                cardError={cardError}
              />
            </Elements>
          )}
          
          {!stripeLoading && !clientSecret && cardError && (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p style={{ color: '#EF4444', fontSize: '14px', marginBottom: '12px' }}>{cardError}</p>
              <button 
                onClick={this.createStripePaymentIntent}
                className={s.retryButton}
              >
                Reintentar
              </button>
            </div>
          )}
        </div>
      );
    }

    // Credicard Venezuela (id: 3) - Full card form
    if (paymentMethod === 3) {
      return (
        <div className={s.paymentFormContainer}>
          <div className={s.paymentFormHeader}>
            <div className={s.paymentFormIcon} style={{ background: '#00A651', color: 'white' }}><CreditCard size={20} /></div>
            <div>
              <p className={s.paymentFormTitle}>Tarjeta de Crédito en Bolívares</p>
              <p className={s.paymentFormSubtitle}>Pago seguro con Megasoft</p>
            </div>
          </div>
          {/* Card Number */}
          <div className={cx(s.fieldCard, s.fieldCardFull)} style={{ marginBottom: '12px' }}>
            <label className={s.fieldCardLabel}><CreditCard size={14} /> Número de Tarjeta</label>
            <input
              type="text"
              className={s.fieldCardInput}
              placeholder="4000 0000 0000 0000"
              maxLength={19}
              value={pan}
              onChange={(e) => this.handlePaymentFieldChange('pan', e.target.value)}
            />
          </div>
          {/* CVV & Expiry Grid */}
          <div className={s.paymentFieldsGrid}>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Lock size={14} /> CVV</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="123"
                maxLength={4}
                value={cvv2}
                onChange={(e) => this.handlePaymentFieldChange('cvv2', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Calendar size={14} /> Expiración</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="MMAA"
                maxLength={4}
                value={expdate}
                onChange={(e) => this.handlePaymentFieldChange('expdate', e.target.value)}
              />
            </div>
          </div>
          {/* Cédula */}
          <div className={cx(s.fieldCard, s.fieldCardFull)} style={{ marginTop: '12px' }}>
            <label className={s.fieldCardLabel}><IdCard size={14} /> Cédula de Identidad</label>
            <input
              type="text"
              className={s.fieldCardInput}
              placeholder="V12345678"
              value={cid}
              onChange={(e) => this.handlePaymentFieldChange('cid', e.target.value)}
            />
          </div>
        </div>
      );
    }

    // Zelle (id: 4)
    if (paymentMethod === 4) {
      return (
        <div className={s.paymentFormContainer}>
          <div className={s.paymentFormHeader}>
            <div className={s.paymentFormIcon} style={{ background: 'linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)', color: 'white' }}><Landmark size={20} /></div>
            <div>
              <p className={s.paymentFormTitle}>Zelle</p>
              <p className={s.paymentFormSubtitle}>Transferencia bancaria USA</p>
            </div>
          </div>
          {/* Cédula */}
          <div className={cx(s.fieldCard, s.fieldCardFull)} style={{ marginBottom: '12px' }}>
            <label className={s.fieldCardLabel}><IdCard size={14} /> Cédula de Identidad</label>
            <input
              type="text"
              className={s.fieldCardInput}
              placeholder="V12345678"
              value={cid}
              onChange={(e) => this.handlePaymentFieldChange('cid', e.target.value)}
            />
          </div>
          {/* Banco & Referencia */}
          <div className={s.paymentFieldsGrid}>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Landmark size={14} /> Banco (Código)</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="CHAS, BOFA, WELLS..."
                value={codigobancoComercio}
                onChange={(e) => this.handlePaymentFieldChange('codigobancoComercio', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Hash size={14} /> Referencia/Confirmación</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="Número de confirmación"
                value={referencia}
                onChange={(e) => this.handlePaymentFieldChange('referencia', e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    // Pago Móvil P2C (id: 5) - Full form
    if (paymentMethod === 5) {
      return (
        <div className={s.paymentFormContainer}>
          <div className={s.paymentFormHeader}>
            <div className={s.paymentFormIcon} style={{ background: '#10B981', color: 'white' }}><Phone size={20} /></div>
            <div>
              <p className={s.paymentFormTitle}>Pago Móvil P2C</p>
              <p className={s.paymentFormSubtitle}>Transferencia interbancaria instantánea</p>
            </div>
          </div>
          <div className={s.paymentFieldsGrid}>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Phone size={14} /> Tu Teléfono</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="04141234567"
                maxLength={11}
                value={telefonoCliente}
                onChange={(e) => this.handlePaymentFieldChange('telefonoCliente', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Landmark size={14} /> Tu Banco</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="0102"
                maxLength={4}
                value={codigobancoCliente}
                onChange={(e) => this.handlePaymentFieldChange('codigobancoCliente', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Phone size={14} /> Teléfono Comercio</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="04121234567"
                maxLength={11}
                value={telefonoComercio}
                onChange={(e) => this.handlePaymentFieldChange('telefonoComercio', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Landmark size={14} /> Banco Comercio</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="0102"
                maxLength={4}
                value={codigobancoComercio}
                onChange={(e) => this.handlePaymentFieldChange('codigobancoComercio', e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    // Débito Inmediato (id: 6) - Full form
    if (paymentMethod === 6) {
      return (
        <div className={s.paymentFormContainer}>
          <div className={s.paymentFormHeader}>
            <div className={s.paymentFormIcon} style={{ background: 'linear-gradient(135deg, #EA580C 0%, #F97316 100%)', color: 'white' }}><Zap size={20} /></div>
            <div>
              <p className={s.paymentFormTitle}>Crédito Inmediato</p>
              <p className={s.paymentFormSubtitle}>Pago instantáneo con código</p>
            </div>
          </div>
          {/* Cédula */}
          <div className={cx(s.fieldCard, s.fieldCardFull)} style={{ marginBottom: '12px' }}>
            <label className={s.fieldCardLabel}><IdCard size={14} /> Cédula de Identidad</label>
            <input
              type="text"
              className={s.fieldCardInput}
              placeholder="V12345678"
              value={cid}
              onChange={(e) => this.handlePaymentFieldChange('cid', e.target.value)}
            />
          </div>
          <div className={s.paymentFieldsGrid}>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Landmark size={14} /> Cuenta Origen</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="01020102010201020102"
                value={cuentaOrigen}
                onChange={(e) => this.handlePaymentFieldChange('cuentaOrigen', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Phone size={14} /> Teléfono Origen</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="04141234567"
                maxLength={11}
                value={telefonoOrigen}
                onChange={(e) => this.handlePaymentFieldChange('telefonoOrigen', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Landmark size={14} /> Banco Origen</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="0102"
                maxLength={4}
                value={codigobancoOrigen}
                onChange={(e) => this.handlePaymentFieldChange('codigobancoOrigen', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Landmark size={14} /> Cuenta Destino</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="01020102010201020102"
                value={cuentaDestino}
                onChange={(e) => this.handlePaymentFieldChange('cuentaDestino', e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    // C2P (id: 7) - Full form
    if (paymentMethod === 7) {
      return (
        <div className={s.paymentFormContainer}>
          <div className={s.paymentFormHeader}>
            <div className={s.paymentFormIcon} style={{ background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)', color: 'white' }}><Phone size={20} /></div>
            <div>
              <p className={s.paymentFormTitle}>Pago Móvil C2P</p>
              <p className={s.paymentFormSubtitle}>Cuenta a Pago Móvil</p>
            </div>
          </div>
          <div className={s.paymentFieldsGrid}>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><IdCard size={14} /> Cédula</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="V12345678"
                value={cid}
                onChange={(e) => this.handlePaymentFieldChange('cid', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}><Phone size={14} /> Teléfono</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="04141234567"
                maxLength={11}
                value={telefono}
                onChange={(e) => this.handlePaymentFieldChange('telefono', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}>🏦 Código Banco</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="0102"
                maxLength={4}
                value={codigobanco}
                onChange={(e) => this.handlePaymentFieldChange('codigobanco', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}>🔑 Código C2P</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="Código de autorización"
                value={codigoC2P}
                onChange={(e) => this.handlePaymentFieldChange('codigoC2P', e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  handleSubmit = async () => {
    const { listId, client, onSuccess, onClose } = this.props;
    const {
      commissionRate, autoRenew, featuredSettings, paymentMethod,
      // Billing fields
      billingCountry, billingName, billingZipcode,
      // All payment fields
      pan, cvv2, expdate, referencia,
      telefonoCliente, codigobancoCliente, telefonoComercio, codigobancoComercio,
      cuentaOrigen, telefonoOrigen, codigobancoOrigen, cuentaDestino,
      telefono, codigobanco, codigoC2P, cid
    } = this.state;
    // El total a cobrar es SOLO la mensualidad
    const selectedTier = this.state.selectedTier;
    const monthlyPrice = selectedTier === 'premium'
      ? (featuredSettings?.premiumMonthlyPrice || 199)
      : (featuredSettings?.monthlyPrice || 99);
    const totalAmount = monthlyPrice;

    // Validate billing country is selected
    if (!billingCountry) {
      showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Por favor selecciona tu país' });
      return;
    }

    // Validate fields based on payment method
    if (paymentMethod === 3) { // Credicard
      if (!pan || !cvv2 || !expdate || !cid) {
        showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Completa todos los datos de la tarjeta' });
        return;
      }
    }
    if (paymentMethod === 4) { // Zelle
      if (!cid || !referencia) {
        showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Ingresa tu cédula y número de referencia' });
        return;
      }
    }
    if (paymentMethod === 5) { // Pago Móvil
      if (!telefonoCliente || !codigobancoCliente) {
        showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Completa todos los campos de Pago Móvil' });
        return;
      }
    }
    if (paymentMethod === 6) { // Débito Inmediato
      if (!cid || !cuentaOrigen || !codigobancoOrigen) {
        showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Completa todos los campos de Débito Inmediato' });
        return;
      }
    }
    if (paymentMethod === 7) { // C2P
      if (!cid || !telefono || !codigobanco) {
        showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Completa todos los campos de C2P' });
        return;
      }
    }

    this.setState({ isLoading: true, cardError: null });

    try {
      // Generate transaction ID and build voucher data for Megasoft
      let transactionId = '';
      let paymentTypeLabel = '';
      let megasoftVoucher = null;

      // Stripe payment - call the exposed function from PaymentElement
      if (paymentMethod === 2) {
        if (typeof window.stripeSubmitPayment === 'function') {
          window.stripeSubmitPayment();
        } else {
          this.setState({ isLoading: false, cardError: 'Stripe no está listo. Intenta de nuevo.' });
        }
        return;
      }

      // Process PayPal - redirect to PayPal
      if (paymentMethod === 1) {
        const paypalResult = await client.mutate({
          mutation: CREATE_PAYPAL_ORDER,
          variables: {
            listId,
            commissionRate,
            autoRenew,
            billingCountry,
            billingName,
            billingZipcode,
            tier: this.state.selectedTier
          }
        });

        if (paypalResult?.data?.createFeaturedPayPalOrder?.status === 'success') {
          const approvalUrl = paypalResult.data.createFeaturedPayPalOrder.approvalUrl;
          if (approvalUrl) {
            // Redirect to PayPal - billing data will be passed in URL for success page
            window.location.href = approvalUrl;
            return;
          }
        } else {
          const errorMsg = paypalResult?.data?.createFeaturedPayPalOrder?.errorMessage || 'Error al conectar con PayPal';
          showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: errorMsg });
          this.setState({ isLoading: false });
          return;
        }
      }

      switch (paymentMethod) {
        case 1:
          // PayPal handled above with redirect
          return;
        case 2:
          // transactionId already set from Stripe token above
          if (!transactionId) transactionId = `stripe_${Date.now()}_${listId}`;
          paymentTypeLabel = 'Stripe';
          break;
        case 3: {
          // MEGASOFT CREDIT CARD - Process payment first
          paymentTypeLabel = 'Tarjeta de Crédito';
          const ccResponse = await fetch('/web-api/pay-with-megasoft', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              pan,
              cvv2,
              cid,
              expdate,
              client: billingName || 'Cliente',
              listId
            })
          });
          const ccResult = await ccResponse.json();
          if (!ccResult.success) {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: ccResult.error || 'Error en el pago con tarjeta' });
            this.setState({ isLoading: false });
            return;
          }
          transactionId = ccResult.data?.voucher?.[0] || ccResult.data?.control?.[0] || `cc_${Date.now()}`;
          megasoftVoucher = ccResult.data;
          break;
        }
        case 4: {
          // MEGASOFT ZELLE - Process payment first
          paymentTypeLabel = 'Zelle';
          const zelleResponse = await fetch('/web-api/pay-with-megasoft-Zelle', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              cid,
              codigobancoComercio,
              referencia,
              listId
            })
          });
          const zelleResult = await zelleResponse.json();
          if (!zelleResult.success) {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: zelleResult.error || 'Error en el pago con Zelle' });
            this.setState({ isLoading: false });
            return;
          }
          transactionId = zelleResult.data?.voucher?.[0] || zelleResult.data?.control?.[0] || `zelle_${Date.now()}`;
          megasoftVoucher = zelleResult.data;
          break;
        }
        case 5: {
          // MEGASOFT PAGO MOVIL P2C - Process payment first
          paymentTypeLabel = 'Pago Móvil';
          const p2cResponse = await fetch('/web-api/pay-with-megasoft-pogo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              telefonoCliente,
              codigobancoCliente,
              telefonoComercio,
              codigobancoComercio,
              listId
            })
          });
          const p2cResult = await p2cResponse.json();
          if (!p2cResult.success) {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: p2cResult.error || 'Error en Pago Móvil' });
            this.setState({ isLoading: false });
            return;
          }
          transactionId = p2cResult.data?.voucher?.[0] || p2cResult.data?.control?.[0] || `p2c_${Date.now()}`;
          megasoftVoucher = p2cResult.data;
          break;
        }
        case 6: {
          // MEGASOFT DEBITO INMEDIATO - Process payment first
          paymentTypeLabel = 'Débito Inmediato';
          const debitResponse = await fetch('/web-api/pay-with-megasoft-creditoinmediato', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              cid,
              cuentaOrigen,
              telefonoOrigen,
              codigobancoOrigen,
              cuentaDestino,
              listId
            })
          });
          const debitResult = await debitResponse.json();
          if (!debitResult.success) {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: debitResult.error || 'Error en Débito Inmediato' });
            this.setState({ isLoading: false });
            return;
          }
          transactionId = debitResult.data?.voucher?.[0] || debitResult.data?.control?.[0] || `debit_${Date.now()}`;
          megasoftVoucher = debitResult.data;
          break;
        }
        case 7: {
          // MEGASOFT C2P - Process payment first
          paymentTypeLabel = 'C2P';
          const c2pResponse = await fetch('/web-api/pay-with-megasoft-c2p', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: totalAmount,
              cid,
              telefono,
              codigobanco,
              codigoC2P,
              listId
            })
          });
          const c2pResult = await c2pResponse.json();
          if (!c2pResult.success) {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: c2pResult.error || 'Error en C2P' });
            this.setState({ isLoading: false });
            return;
          }
          transactionId = c2pResult.data?.voucher?.[0] || c2pResult.data?.control?.[0] || `c2p_${Date.now()}`;
          megasoftVoucher = c2pResult.data;
          break;
        }
        default:
          transactionId = `payment_${Date.now()}_${listId}`;
          paymentTypeLabel = 'Payment';
      }

      // Create the featured listing with the payment info
      const result = await client.mutate({
        mutation: CREATE_FEATURED_LISTING,
        variables: {
          listId,
          commissionRate,
          autoRenew,
          paymentType: paymentMethod,
          transactionId,
          megasoftVoucher: megasoftVoucher ? JSON.stringify(megasoftVoucher) : null,
          billingCountry,
          billingName,
          billingZipcode,
          tier: this.state.selectedTier
        }
      });

      if (result?.data?.createFeaturedListing?.status === 'success') {
        showToaster({
          messageId: 'featuredSuccess',
          toasterType: 'success',
          requestMessage: `¡Pago procesado con ${paymentTypeLabel}!`
        });
        onSuccess && onSuccess();
        onClose();
      } else {
        showToaster({
          messageId: 'commonError',
          toasterType: 'error',
          requestMessage: result?.data?.createFeaturedListing?.errorMessage
        });
      }
    } catch (error) {
      console.error('Error creating featured listing:', error);
      showToaster({ messageId: 'commonError', toasterType: 'error' });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleCancel = async () => {
    const { client, onSuccess, onClose } = this.props;
    const { featuredStatus } = this.state;

    if (!featuredStatus) return;

    this.setState({ isLoading: true });

    try {
      const result = await client.mutate({
        mutation: CANCEL_FEATURED,
        variables: {
          featuredListingId: featuredStatus.id
        }
      });

      if (result?.data?.cancelFeaturedListing?.status === 'success') {
        showToaster({ messageId: 'updateSuccess', toasterType: 'success' });
        onSuccess && onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error cancelling featured:', error);
      showToaster({ messageId: 'commonError', toasterType: 'error' });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  renderActiveStatus() {
    const { featuredStatus, autoRenew } = this.state;
    const { formatMessage } = this.props.intl;

    return (
      <div>
        <div className={s.statusInfo}>
          <p className={s.statusText}>
            ★ <FormattedMessage {...messages.featured} /> - {featuredStatus.daysRemaining} <FormattedMessage {...messages.daysRemaining} />
          </p>
        </div>

        <div className={s.formSection}>
          <div className={s.totalRow}>
            <span className={s.totalLabel}><FormattedMessage {...messages.remainingBid} /></span>
            <span className={s.totalValue}>${parseFloat(featuredStatus.remainingBid || 0).toFixed(2)}</span>
          </div>
          <div className={s.totalRow}>
            <span className={s.totalLabel}><FormattedMessage {...messages.monthlyFee} /></span>
            <span className={s.totalValue}>${parseFloat(featuredStatus.monthlyFee || 0).toFixed(2)}</span>
          </div>
        </div>

        <div className={s.formSection}>
          <div className={s.toggleWrapper}>
            <span className={s.toggleLabel}><FormattedMessage {...messages.autoRenew} /></span>
            <label className={s.toggleSwitch}>
              <input
                type="checkbox"
                className={s.toggleInput}
                checked={autoRenew}
                onChange={this.handleAutoRenewChange}
              />
              <span className={s.toggleSlider}></span>
            </label>
          </div>
          <p className={s.formDescription}>
            <FormattedMessage {...messages.autoRenewDescription} />
          </p>
        </div>

        <button
          className={s.payButton}
          onClick={this.handleCancel}
          disabled={this.state.isLoading}
          style={{ backgroundColor: '#DC2626' }}
        >
          {this.state.isLoading && <span className={s.loadingSpinner}></span>}
          <FormattedMessage {...messages.cancelFeatured} />
        </button>
      </div>
    );
  }

  renderNewFeatured() {
    const { commissionRate, autoRenew, featuredSettings, isLoading, selectedTier,
      competitors, currentPosition, totalCompetitors, rateToBeFirst } = this.state;
    const { formatMessage } = this.props.intl;

    const standardPrice = featuredSettings?.monthlyPrice || 99;
    const premiumPrice = featuredSettings?.premiumMonthlyPrice || 199;
    const monthlyPrice = selectedTier === 'premium' ? premiumPrice : standardPrice;
    const total = monthlyPrice;

    return (
      <div>
        {/* Tier Selection */}
        <div className={s.formSection}>
          <label className={s.formLabel}>Selecciona tu Plan</label>
          <div className={s.tierGrid}>
            {/* Standard Tier */}
            <div
              className={cx(s.tierCard, { [s.tierCardActive]: selectedTier === 'standard' })}
              onClick={() => {
                if (selectedTier !== 'standard') {
                  this.setState({ selectedTier: 'standard' });
                }
              }}
            >
              <div className={s.tierHeader}>
                <Star size={20} />
                <span className={s.tierName}>Standard</span>
              </div>
              <div className={s.tierPrice}>
                <span className={s.tierAmount}>${standardPrice}</span>
                <span className={s.tierPeriod}>/mes</span>
              </div>
              <ul className={s.tierFeatures}>
                <li>✓ Aparece en página principal</li>
                <li>✓ Badge "Destacado"</li>
                <li>✓ Posicionamiento por bid</li>
              </ul>
            </div>

            {/* Premium Tier */}
            <div
              className={cx(s.tierCard, s.tierCardPremium, { [s.tierCardActive]: selectedTier === 'premium' })}
              onClick={() => {
                if (selectedTier !== 'premium') {
                  this.setState({ selectedTier: 'premium' });
                }
              }}
            >
              <div className={s.tierBadge}>RECOMENDADO</div>
              <div className={s.tierHeader}>
                <Sparkles size={20} />
                <span className={s.tierName}>Premium</span>
              </div>
              <div className={s.tierPrice}>
                <span className={s.tierAmount}>${premiumPrice}</span>
                <span className={s.tierPeriod}>/mes</span>
              </div>
              <ul className={s.tierFeatures}>
                <li>✓ Todo de Standard</li>
                <li>✓ Badge "Premium" dorado</li>
                <li>✓ Prioridad en búsquedas</li>
                <li>✓ Analytics avanzados</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Monthly Fee - Now shows selected tier price */}
        <div className={s.formSection}>
          <label className={s.formLabel}>
            <FormattedMessage {...messages.monthlyFee} /> ({selectedTier === 'premium' ? 'Premium' : 'Standard'})
          </label>
          <div className={s.priceBox}>
            <span className={s.priceLabel}>USD</span>
            <span className={s.priceValue}>${monthlyPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Commission Rate with Position */}
        <div className={s.formSection}>
          <label className={s.formLabel}>
            Comisión por Reserva (opcional)
          </label>
          <p className={s.formDescription} style={{ marginBottom: '12px' }}>
            Entre mayor comisión ofrezcas, mejor posición tendrás. Se cobra solo cuando hay reservas.
          </p>

          {/* Slider */}
          <div className={s.sliderContainer}>
            <input
              type="range"
              className={s.bidSlider}
              min="0"
              max="40"
              step="1"
              value={commissionRate}
              onChange={this.handleCommissionChange}
            />
            <div className={s.sliderLabels}>
              <span>0%</span>
              <span>20%</span>
              <span>40%</span>
            </div>
          </div>

          {/* Manual Input */}
          <div className={s.bidInputWrapper}>
            <input
              type="number"
              className={s.bidInput}
              value={commissionRate}
              onChange={this.handleCommissionChange}
              min="0"
              max="40"
              step="1"
              placeholder="0"
            />
            <span className={s.bidCurrency}>%</span>
          </div>

          {/* Position Preview */}
          {totalCompetitors > 0 && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#F0FDF4',
              borderRadius: '12px',
              border: '1px solid #86EFAC'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#166534' }}>
                  Tu posición: #{currentPosition || '?'} de {totalCompetitors}
                </span>
                {currentPosition === 1 ? (
                  <span style={{ background: '#22C55E', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                    🏆 LÍDER
                  </span>
                ) : rateToBeFirst > commissionRate && (
                  <span style={{ color: '#166534', fontSize: '13px' }}>
                    Necesitas {rateToBeFirst}% para ser #1
                  </span>
                )}
              </div>

              {/* Position Bar */}
              <div style={{
                height: '8px',
                backgroundColor: '#D1FAE5',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: `${Math.max(5, ((totalCompetitors - currentPosition + 1) / totalCompetitors) * 100)}%`,
                  height: '100%',
                  backgroundColor: currentPosition === 1 ? '#22C55E' : '#10B981',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>

              {/* Top 5 Competitors */}
              {competitors.length > 0 && (
                <div style={{ fontSize: '13px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151' }}>Top Competidores:</div>
                  {competitors.slice(0, 5).map((comp, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 0',
                      borderBottom: idx < 4 ? '1px solid #E5E7EB' : 'none',
                      backgroundColor: comp.isCurrentUser ? '#DCFCE7' : 'transparent',
                      marginLeft: comp.isCurrentUser ? '-8px' : '0',
                      marginRight: comp.isCurrentUser ? '-8px' : '0',
                      paddingLeft: comp.isCurrentUser ? '8px' : '0',
                      paddingRight: comp.isCurrentUser ? '8px' : '0',
                      borderRadius: comp.isCurrentUser ? '4px' : '0'
                    }}>
                      <span style={{ color: comp.isCurrentUser ? '#166534' : '#6B7280' }}>
                        #{comp.position} {comp.listTitle?.substring(0, 25)}{comp.listTitle?.length > 25 ? '...' : ''}
                        {comp.isCurrentUser && ' (Tú)'}
                      </span>
                      <span style={{ fontWeight: '600', color: '#166534' }}>{comp.commissionRate}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Auto Renew */}
        <div className={s.formSection}>
          <div className={s.toggleWrapper}>
            <span className={s.toggleLabel}><FormattedMessage {...messages.autoRenew} /></span>
            <label className={s.toggleSwitch}>
              <input
                type="checkbox"
                className={s.toggleInput}
                checked={autoRenew}
                onChange={this.handleAutoRenewChange}
              />
              <span className={s.toggleSlider}></span>
            </label>
          </div>
          <p className={s.formDescription}>
            <FormattedMessage {...messages.autoRenewDescription} />
          </p>
        </div>

        {/* Total - Solo mensualidad */}
        <div className={s.totalSection}>
          <div className={s.totalRow}>
            <span className={s.totalLabel}><FormattedMessage {...messages.monthlyFee} /></span>
            <span className={s.totalValue}>${monthlyPrice.toFixed(2)}</span>
          </div>
          {commissionRate > 0 && (
            <div className={s.totalRow} style={{ opacity: 0.7 }}>
              <span className={s.totalLabel}>Comisión configurada (se cobra por reserva)</span>
              <span className={s.totalValue}>{commissionRate}%</span>
            </div>
          )}
          <div className={s.grandTotal}>
            <span className={s.grandTotalLabel}><FormattedMessage {...messages.totalToPay} /></span>
            <span className={s.grandTotalValue}>${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Billing Address Section */}
        <div className={s.formSection}>
          <h4 className={s.sectionTitle}>Dirección de Facturación</h4>
          <div className={s.paymentFieldsGrid}>
            <div className={cx(s.fieldCard, s.fieldCardFull)}>
              <label className={s.fieldCardLabel}>País</label>
              <select
                className={s.fieldCardInput}
                value={this.state.billingCountry}
                onChange={(e) => {
                  const newCountry = e.target.value;
                  this.setState({ billingCountry: newCountry });
                  // Reset to Stripe if switching from VE and current method is Megasoft
                  if (newCountry !== 'VE' && [3, 4, 5, 6, 7].includes(this.state.paymentMethod)) {
                    this.setState({ paymentMethod: 2 });
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <option value="">Selecciona un país</option>
                {this.state.countries && this.state.countries.length > 0 &&
                  this.state.countries
                    .filter(c => c.isEnable)
                    .map((country) => (
                      <option key={country.id} value={country.countryCode}>
                        {country.countryName}
                      </option>
                    ))
                }
              </select>
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}>Nombre Completo</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="Juan Pérez"
                value={this.state.billingName}
                onChange={(e) => this.handlePaymentFieldChange('billingName', e.target.value)}
              />
            </div>
            <div className={s.fieldCard}>
              <label className={s.fieldCardLabel}>Código Postal</label>
              <input
                type="text"
                className={s.fieldCardInput}
                placeholder="1010"
                value={this.state.billingZipcode}
                onChange={(e) => this.handlePaymentFieldChange('billingZipcode', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Payment Method Selector */}
        <div className={s.paymentMethodSection}>
          <h4 className={s.sectionTitle}>Método de Pago</h4>
          {this.state.billingCountry !== 'VE' && this.state.billingCountry !== '' && (
            <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
              Para pagos fuera de Venezuela, solo están disponibles PayPal y tarjetas internacionales.
            </p>
          )}
          <div className={s.paymentMethods}>
            {this.getAvailablePaymentMethods().map((method) => {
              const IconComponent = method.icon;
              const isSelected = this.state.paymentMethod === method.id;
              const isStringIcon = typeof method.icon === 'string';
              return (
                <div
                  key={method.id}
                  className={cx(
                    s.paymentMethodOption,
                    isSelected && s.paymentMethodOptionSelected
                  )}
                  onClick={() => this.handlePaymentMethodChange(method.id)}
                  style={isSelected ? { borderColor: method.color } : {}}
                >
                  <div className={s.paymentMethodIcon} style={isSelected ? { backgroundColor: method.color + '20', color: method.color } : {}}>
                    {isStringIcon ? method.icon : <IconComponent size={20} />}
                  </div>
                  <div className={s.paymentMethodInfo}>
                    <p className={s.paymentMethodName}>{method.name}</p>
                    <p className={s.paymentMethodDesc}>{method.desc}</p>
                  </div>
                  <div className={s.radioCircle} style={isSelected ? { borderColor: method.color, backgroundColor: method.color } : {}} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment Form based on selected method */}
        {this.renderPaymentForm()}
      </div>
    );
  }

  renderActivateButton() {
    const { isLoading } = this.state;
    return (
      <button
        className={s.payButtonGold}
        onClick={this.handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? (
          <span className={s.loadingSpinner}></span>
        ) : (
          <Zap className={s.payButtonIcon} />
        )}
        Activar Destacado
      </button>
    );
  }

  render() {
    const { isOpen, onClose, listTitle, listPhoto, listCity } = this.props;
    const { featuredStatus, featuredSettings } = this.state;

    if (!isOpen) return null;

    const isActive = featuredStatus && featuredStatus.status === 'active';

    // Use createPortal to render modal outside component tree (fixes z-index stacking context issues)
    const modalContent = (
      <div className={s.modalOverlay} onClick={onClose}>
        <div className={s.modalContainer} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={s.modalHeader}>
            <h2 className={s.modalTitle}>
              <Sparkles size={24} style={{ marginRight: 8, color: '#F59E0B' }} />
              <FormattedMessage {...messages.featureYourBoat} />
            </h2>
            <button className={s.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className={s.modalBody}>
            {/* Listing Info */}
            <div className={s.listingInfo}>
              {listPhoto && (
                <img
                  src={`/images/upload/x_medium_${listPhoto}`}
                  alt={listTitle}
                  className={s.listingImage}
                />
              )}
              <div>
                <h3 className={s.listingTitle}>{listTitle}</h3>
                {listCity && <p className={s.listingLocation}>{listCity}</p>}
              </div>
            </div>

            {/* Content */}
            {featuredSettings && (
              isActive ? this.renderActiveStatus() : this.renderNewFeatured()
            )}
          </div>

          {/* Footer with sticky button */}
          {featuredSettings && !isActive && (
            <div className={s.modalFooter}>
              {this.renderActivateButton()}
            </div>
          )}
        </div>
      </div>
    );

    // Render modal in document.body using portal to escape stacking context
    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(modalContent, document.body);
    }
    return modalContent;
  }
}

const mapState = (state) => ({});

const mapDispatch = {};

export default compose(
  injectIntl,
  withStyles(s),
  withApollo,
  connect(mapState, mapDispatch),
)(FeaturedModal);
